import { gpCommon } from '../app';
import { ModalService, IUibModalInstance } from '../services/bootstrap-ui-modal';
import { CapabilityKey } from '../util/capabilities';
import { displayNameForCapabilityKey } from '../util/capability-catalogue';
import { capabilityReasonFor } from '../util/capability-context';
import { gateFromReason, permitsAction } from '../util/capability-presentation';
import { isCapabilityGatingEnabled } from '../util/feature-flags';

import capabilityUpsellDialogTemplate from './capability-upsell-dialog.html';

import type * as ng from 'angular';

/**
 * The upsell (issue #566) — what a withheld-but-live control produces when the
 * user reaches for it.
 *
 * ONE STEP BEYOND THE INTERACTION, and that is the whole design. The pitch is
 * never on the control: a menu item reading "Primavera P6 (Pro)" tells the user
 * "no" before they have said what they want, and it makes every menu a price
 * list. Instead the control looks and behaves exactly as it does for an
 * entitled user, the click is allowed, and the thing it produces does the
 * selling — with room to say what the feature is and a way to act on it.
 *
 * Where the control has a natural destination that can carry the pitch itself
 * (the import dialog, the Costs tab) the destination hosts a `gp-if-withheld`
 * panel and this dialog is not involved. Where it does not — an export that
 * simply downloads a file — this dialog IS the destination.
 *
 * The action is `window.open` to the account page rather than an in-app route:
 * the workspace holds unsaved plan state and is expensive to reload, and the
 * account page lives in a different Angular app (`app-home`), so navigating
 * there in place would tear the workspace down to show a price list.
 */

/**
 * Where "See plans" goes. The same URL the toolbar's licence banner and the
 * Plan menu's upgrade row already use, so there is one account destination
 * rather than three.
 */
export const CAPABILITY_UPSELL_ACCOUNT_URL = '/#/account';

interface ICapabilityUpsellDialogScope extends ng.IScope {
    title: string;
    capabilityName: string;
    seePlans(): void;
    close(): void;
}

gpCommon.controller('gpCapabilityUpsellDialog', [
    '$scope',
    '$uibModalInstance',
    'capabilityKey',
    function (
        $scope: ICapabilityUpsellDialogScope,
        $uibModalInstance: IUibModalInstance<string>,
        capabilityKey: CapabilityKey) {

        $scope.capabilityName = displayNameForCapabilityKey(capabilityKey);
        $scope.title = 'Upgrade to use this';

        $scope.seePlans = () => {
            window.open(CAPABILITY_UPSELL_ACCOUNT_URL, '_blank');
            $uibModalInstance.close('seePlans');
        };

        $scope.close = () => $uibModalInstance.dismiss('cancel');
    }]);

export class CapabilityUpsellService {
    static $inject = ['gpModal', '$injector'];
    static readonly serviceName = 'gpCapabilityUpsell';

    constructor(
        private readonly modalService: ModalService,
        private readonly $injector: ng.auto.IInjectorService) {
    }

    /**
     * May this capability be used right now? Call it at the TOP of the action a
     * gated control triggers, and return when it says no:
     *
     *     if (!this.gpCapabilityUpsell.allow('primaveraImport')) { return; }
     *
     * Three answers, not two, and the third is why this is not just `gpCanUse`:
     *
     * - available (or no answer at all) — true, do the thing.
     * - withheld by LICENCE — false, and the upsell dialog opens. Upgrading
     *   fixes this, so there is something worth saying.
     * - withheld by PLAN ACCESS — false, and NOTHING opens. Upgrading does not
     *   fix a plan-access shortfall, so a pitch there would be a lie. The
     *   control that led here should already have been hidden by the directive;
     *   this is the second lock on a door that should not have been reachable.
     */
    public allow(key: CapabilityKey): boolean {
        if (!isCapabilityGatingEnabled()) {
            return true;
        }

        // Read once. Deciding whether to permit and deciding what to say about
        // a refusal are two answers to the same question, and a snapshot that
        // refilled between two reads could answer them differently.
        const reason = capabilityReasonFor(this.$injector, key);

        if (permitsAction(reason)) {
            return true;
        }

        if (gateFromReason(reason) === 'withheldByLicence') {
            this.show(key);
        }

        return false;
    }

    /**
     * The same verdict as `allow`, without the dialog. For a destination that
     * carries its own `gp-if-withheld` panel — the baselines dialog, the Costs
     * tab — where the pitch is already on screen and a modal on top of it would
     * sell the same upgrade twice.
     *
     * Also the value to bind a control's disabled state to, so what the user
     * can see and what the handler will do cannot disagree.
     */
    public permits(key: CapabilityKey): boolean {
        return !isCapabilityGatingEnabled()
            || permitsAction(capabilityReasonFor(this.$injector, key));
    }

    /** Opens the upsell unconditionally. Prefer `allow`, which decides first. */
    public show(key: CapabilityKey): ng.IPromise<string> {
        return this.modalService.open<string>({
            animation: true,
            template: capabilityUpsellDialogTemplate,
            controller: 'gpCapabilityUpsellDialog',
            backdrop: true,
            backdropClass: 'gameplan-capability-upsell-dialog',
            windowClass: 'gameplan-capability-upsell-dialog',
            resolve: {
                capabilityKey: (): CapabilityKey => key,
            },
        }).result;
    }
}

gpCommon.service(CapabilityUpsellService.serviceName, CapabilityUpsellService);
